<template>
  <div class="cps">
    <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />
    <div class="cps__body">
      <!-- panel header already shows the title/icon; only the hint here -->
      <p class="cps__intro-sub">{{ $t('customerPanelHint') }}</p>

      <section class="cps__card">
        <div class="cps__card-head">
          <v-icon small color="primary">mdi-link-variant</v-icon>
          <span>{{ $t('customerPanelLink') }}</span>
        </div>
        <div class="cps__link">
          <p class="cps__hint cps__link-hint">{{ $t('customerPanelLinkHint') }}</p>
          <p v-if="!panelUrl" class="cps__link-empty">{{ loading ? '…' : $t('customerPanelLinkEmpty') }}</p>
          <div v-else class="cps__link-row">
            <a :href="panelUrl" target="_blank" rel="noopener" class="cps__link-url" dir="ltr">{{ panelUrl }}</a>
            <button type="button" class="cps__link-btn" :title="$t('copy')" @click="copyLink">
              <v-icon small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </button>
            <button type="button" class="cps__link-btn" :title="$t('customerPanelShare')" @click="shareLink">
              <v-icon small>mdi-share-variant-outline</v-icon>
            </button>
          </div>
        </div>
      </section>

      <section class="cps__card">
        <div class="cps__card-head">
          <v-icon small color="primary">mdi-file-document-edit-outline</v-icon>
          <span>{{ $t('customerPanelInvoices') }}</span>
        </div>
        <div class="cps__rows">
          <div v-for="item in items" :key="item.key" class="cps__row">
            <setting-toggle-row :form="form" :item="item" />
            <p v-if="item.hint" class="cps__hint">{{ $t(item.hint) }}</p>
          </div>
        </div>
      </section>

      <p v-if="loadError" class="cps__error">
        <v-icon small color="error">mdi-alert-circle-outline</v-icon>{{ $t('customerPanelLoadError') }}
        <button type="button" class="cps__retry" @click="load">{{ $t('refresh') }}</button>
      </p>
    </div>

    <div class="cps__foot">
      <v-btn color="primary" depressed class="cps__submit" :loading="saving" :disabled="loading" @click="save">
        <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('submit') }}
      </v-btn>
      <span v-if="dirty" class="cps__dirty">{{ $t('customerPanelUnsaved') }}</span>
    </div>

    <v-snackbar v-model="snack.show" :color="snack.color" :timeout="2500" top>
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
// Customer panel settings (config_control keys). Loads with
// GET setting?getall=1 and saves with PUT setting/0
// { configControl: true, items: [{ key, value }] } — see
// docs/api/config-control-settings.md. Not part of the general settings
// payload: this module owns its own state and save.
import SettingToggleRow from './SettingToggleRow'
import { customerPanelItems } from '../settingSchema'

// GET setting?getall=1 shape is not documented: accept an array of
// { key, value }, { data: [...] }, or a plain { key: value } map.
function toMap (res) {
  const body = res && res.data !== undefined ? res.data : res
  const list = Array.isArray(body) ? body : Array.isArray(body && body.data) ? body.data : null
  const map = {}
  if (list) {
    list.forEach(r => { if (r && r.key !== undefined) map[r.key] = r.value })
  } else if (body && typeof body === 'object') {
    Object.keys(body).forEach(k => { map[k] = body[k] && typeof body[k] === 'object' && 'value' in body[k] ? body[k].value : body[k] })
  }
  return map
}

// Finds a config key anywhere in the getall response (row list, map or
// nested object), since its exact shape is not documented.
function findKey (node, key, depth = 0) {
  if (!node || typeof node !== 'object' || depth > 4) return null
  if (Array.isArray(node)) {
    for (const r of node) {
      if (r && r.key === key) return r.value || null
      const v = findKey(r, key, depth + 1)
      if (v) return v
    }
    return null
  }
  if (key in node) {
    const v = node[key]
    return v && typeof v === 'object' ? v.value || null : v || null
  }
  for (const k of Object.keys(node)) {
    const v = findKey(node[k], key, depth + 1)
    if (v) return v
  }
  return null
}

export default {
  name: 'CustomerPanelSettings',
  components: { SettingToggleRow },
  data () {
    const form = {}
    customerPanelItems.forEach(i => { form[i.key] = null })
    return {
      items: customerPanelItems,
      form,
      saved: { ...form },
      panelAddress: null,
      copied: false,
      loading: false,
      saving: false,
      loadError: false,
      snack: { show: false, text: '', color: 'success' },
    }
  },
  computed: {
    // customerPanelAddress may come without a scheme.
    panelUrl () {
      const a = this.panelAddress && String(this.panelAddress).trim()
      if (!a) return null
      return /^https?:\/\//i.test(a) ? a : 'https://' + a
    },
    dirty () {
      return this.items.some(i => this.form[i.key] !== this.saved[i.key])
    },
  },
  created () {
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      this.loadError = false
      this.$axios.get(this.$endpoint + 'setting', { params: { getall: 1 } })
        .then(res => {
          const map = toMap(res)
          this.items.forEach(i => {
            this.form[i.key] = map[i.key] === undefined ? null : map[i.key]
          })
          this.saved = { ...this.form }
          this.panelAddress = findKey(res && res.data, 'customerPanelAddress')
        })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    copyLink () {
      const done = () => {
        this.copied = true
        this.snack = { show: true, text: this.$t('customerPanelLinkCopied'), color: 'success' }
        setTimeout(() => { this.copied = false }, 2000)
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.panelUrl).then(done).catch(() => this.copyFallback(done))
      } else {
        this.copyFallback(done)
      }
    },
    copyFallback (done) {
      const ta = document.createElement('textarea')
      ta.value = this.panelUrl
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy'); done() } catch (e) {}
      document.body.removeChild(ta)
    },
    // Native share sheet where supported (mostly mobile), else copy.
    shareLink () {
      if (navigator.share) {
        navigator.share({ title: this.$t('customerPanelLink'), url: this.panelUrl }).catch(() => {})
      } else {
        this.copyLink()
      }
    },
    save () {
      this.saving = true
      this.$axios.put(this.$endpoint + 'setting/0', {
        configControl: true,
        items: this.items.map(i => ({ key: i.key, value: this.form[i.key] })),
      })
        .then(() => {
          this.saved = { ...this.form }
          this.snack = { show: true, text: this.$t('alert-edited'), color: 'success' }
        })
        .catch(err => {
          const msg = err && err.response && typeof err.response.data === 'string' ? err.response.data : this.$t('customerPanelSaveError')
          this.snack = { show: true, text: msg, color: 'error' }
        })
        .finally(() => { this.saving = false })
    },
  },
}
</script>

<style scoped>
.cps {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.cps__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.cps__body > * {
  flex-shrink: 0;
}
.cps__intro-sub {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.7;
}
.cps__card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}
.theme--dark .cps__card {
  background: #1e1e1e;
  border-color: #333;
}
.cps__card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-weight: 600;
  font-size: 0.88rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.cps__row + .cps__row {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.cps__hint {
  margin: -6px 0 0;
  padding: 0 14px 10px;
  font-size: 0.76rem;
  color: #8a94a6;
  line-height: 1.7;
}
.cps__link {
  padding: 12px 14px 14px;
}
.cps__link-hint {
  margin: 0 0 10px;
  padding: 0;
}
.cps__link-empty {
  margin: 0;
  padding: 8px 12px;
  border: 1px dashed #dfe3ea;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #8a94a6;
}
.cps__link-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cps__link-url {
  flex: 1 1 auto;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #dfe3ea;
  border-radius: 6px;
  background: #f8f9fb;
  font-size: 0.82rem;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}
.theme--dark .cps__link-url {
  background: #121212;
  border-color: #3a3a3a;
}
.cps__link-btn {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border: 1px solid #dfe3ea;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cps__link-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}
.theme--dark .cps__link-btn {
  border-color: #3a3a3a;
}
.cps__error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.8rem;
  color: #c62828;
}
.cps__retry {
  color: var(--v-primary-base);
  text-decoration: underline;
}
.cps__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
}
.cps__submit {
  border-radius: 6px;
}
.cps__dirty {
  font-size: 0.76rem;
  color: #b26a00;
}
@media (max-width: 799px) {
  .cps__body {
    padding: 12px;
  }
  .cps__foot {
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
  }
  .cps__submit {
    flex: 1 1 auto;
  }
}
</style>
